import { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import { useServerProducts } from '@/hooks/useServerProducts';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

export const SearchDropdown = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Only fetch when dropdown is open and there's a search query
  const { data, isLoading: loading } = useServerProducts({
    search: debouncedSearch,
    limit: 6,
    page: 1,
    language
  });

  const searchResults = useMemo(() => {
    if (!debouncedSearch.trim() || !data?.products) return [];
    return data.products;
  }, [debouncedSearch, data?.products]);

  const isDiscountActive = (product: any) => {
    if (!product.on_discount || !product.discount_percentage) return false;
    const now = new Date();
    if (product.discount_start_date && new Date(product.discount_start_date) > now) return false;
    if (product.discount_end_date && new Date(product.discount_end_date) < now) return false;
    return true;
  };

  const calculateDiscountedPrice = (product: any) => {
    if (!isDiscountActive(product)) return product.price;
    return product.price * (1 - (product.discount_percentage || 0) / 100);
  };

  const searchButtonLabel = language === 'sv' ? 'Sök' : language === 'ar' ? 'بحث' : 'Search';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={searchButtonLabel}>
          <Search className="h-5 w-5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[350px] md:w-[450px] p-0 bg-background border-border z-[100]"
        sideOffset={5}
      >
        <div className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={language === 'sv' ? 'Sök produkter...' : language === 'ar' ? 'ابحث عن المنتجات...' : 'Search products...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
              autoFocus
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
                onClick={() => setSearchQuery('')}
                aria-label={language === 'sv' ? 'Rensa sökning' : language === 'ar' ? 'مسح البحث' : 'Clear search'}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {loading && debouncedSearch.trim() ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'sv' ? 'Söker...' : language === 'ar' ? 'جاري البحث...' : 'Searching...'}
              </p>
            </div>
          ) : !searchQuery.trim() ? (
            <div className="p-8 text-center">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {language === 'sv' ? 'Börja skriva för att söka produkter' : language === 'ar' ? 'ابدأ بالكتابة للبحث عن المنتجات' : 'Start typing to search products'}
              </p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-muted-foreground">
                {language === 'sv' ? 'Inga produkter hittades' : language === 'ar' ? 'لم يتم العثور على منتجات' : 'No products found'}
              </p>
            </div>
          ) : (
            <div className="py-2">
              {searchResults.map((product: any) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  onClick={() => {
                    setOpen(false);
                    setSearchQuery('');
                  }}
                  className="flex items-center gap-3 p-3 hover:bg-muted transition-colors cursor-pointer"
                >
                  <img
                    src={product.image}
                    alt={product.name[language as 'sv' | 'en' | 'ar']}
                    className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground truncate">
                      {product.name[language as 'sv' | 'en' | 'ar']}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {isDiscountActive(product) ? (
                        <>
                          <span className="text-sm font-semibold text-primary">
                            {calculateDiscountedPrice(product).toFixed(2)} SEK
                          </span>
                          <span className="text-xs text-muted-foreground line-through">
                            {product.price} SEK
                          </span>
                          <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">
                            -{product.discount_percentage}%
                          </span>
                        </>
                      ) : (
                        <span className="text-sm font-semibold text-foreground">
                          {product.price} SEK
                        </span>
                      )}
                    </div>
                    {product.stock_quantity === 0 && (
                      <span className="text-xs text-red-500 mt-1 block">
                        {language === 'sv' ? 'Slut i lager' : language === 'ar' ? 'نفذ من المخزون' : 'Out of stock'}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
              
              {searchResults.length > 0 && (
                <div className="p-3 border-t border-border bg-muted/50">
                  <button
                    onClick={() => {
                      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
                      setOpen(false);
                    }}
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    {language === 'sv' ? 'Visa alla resultat' : language === 'ar' ? 'عرض جميع النتائج' : 'View all results'} →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
